import { useEffect, useState, useMemo } from "react";
import {
  Package,
  Store,
  BarChart3,
  DollarSign,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { useAuth, useSellerDashboard } from "../../hooks";

const formatCurrency = (amount) => {
  const value = Number(amount ?? 0);
  return (
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + " TZS"
  );
};

const formatCompactCurrency = (value) => {
  const n = Number(value ?? 0);
  const abs = Math.abs(n);

  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

const Pagination = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 30, 40, 50, 100, 200],
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-200">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>Rows per page:</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="w-16 h-8 border border-gray-300 rounded-lg bg-white text-sm">
            <SelectValue placeholder="10" />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-2">
          {startItem}-{endItem} of {totalItems}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1 px-2">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "ghost"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className={`h-8 w-8 p-0 text-sm ${
                  currentPage === pageNum
                    ? "bg-blue-800 text-white hover:bg-blue-900"
                    : "hover:bg-gray-100"
                }`}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm shadow-lg">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        {payload.map((p) => (
          <p
            key={p.dataKey}
            style={{ color: p.fill || p.color }}
            className="font-semibold"
          >
            {p.name}: {formatCurrency(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const SellerHome = () => {
  const { user, userAccount } = useAuth();

  const {
    cards,
    chart,
    recentActivities,
    recentActivitiesMeta,
    categoryDistribution,
    availableYears,
    availableShops,
    loading,
    error,
    refresh,
    selectedYear,
    setSelectedYear,
    timeRange,
    setTimeRange,
    shopId,
    setShopId,
    activitiesPage,
    setActivitiesPage,
    activitiesPerPage,
    setActivitiesPerPage,
    setActivitiesSearch,
  } = useSellerDashboard();

  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setActivitiesSearch(searchInput);
      setActivitiesPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, setActivitiesSearch, setActivitiesPage]);

  useEffect(() => {
    document.title = "Seller Dashboard - Shop Kiganjani";
  }, []);

  const displayName = userAccount?.full_name || user?.full_name || "Seller";

  const statCards = useMemo(
    () => [
      {
        title: "Total Products",
        value: String(cards.total_products ?? 0),
        icon: Package,
        iconColor: "text-violet-500",
        iconBg: "bg-violet-100",
      },
      {
        title: "Shops",
        value: String(cards.total_shops ?? 0),
        icon: Store,
        iconColor: "text-teal-500",
        iconBg: "bg-teal-100",
      },
      {
        title: "Total Sales",
        value: formatCurrency(cards.total_sales ?? 0),
        icon: BarChart3,
        iconColor: "text-emerald-500",
        iconBg: "bg-emerald-100",
      },
      {
        title: "Sale Closeout",
        value: formatCurrency(cards.total_closeout ?? 0),
        icon: DollarSign,
        iconColor: "text-amber-500",
        iconBg: "bg-amber-100",
      },
    ],
    [cards],
  );

  if (loading && !chart.length) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center gap-3">
        <p className="text-red-600 text-sm">
          Failed to load dashboard. Please try again.
        </p>
        <Button
          onClick={() => refresh()}
          className="bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {getGreeting()}, {displayName}
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Here is a summary of your sales performance
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {availableShops.length > 0 && (
            <Select
              value={shopId || "all"}
              onValueChange={(v) => setShopId(v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-40 h-9 border border-gray-300 rounded-lg bg-white">
                <SelectValue placeholder="All shops" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All shops</SelectItem>
                {availableShops.map((s) => (
                  <SelectItem key={s.shopId} value={s.shopId}>
                    {s.shop_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTimeRange("daily")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                timeRange === "daily"
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setTimeRange("monthly")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                timeRange === "monthly"
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setTimeRange("yearly")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                timeRange === "yearly"
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              Yearly
            </button>
          </div>

          <Select
            value={String(selectedYear)}
            onValueChange={(v) => setSelectedYear(v)}
          >
            <SelectTrigger className="w-24 h-9 border border-gray-300 rounded-lg bg-white">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {(availableYears.length
                ? availableYears
                : [new Date().getFullYear()]
              ).map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                className="border border-gray-200 bg-white p-5 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      {stat.title}
                    </span>
                    <span className="text-2xl font-bold text-gray-900 truncate">
                      {stat.value}
                    </span>
                  </div>
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-lg ${stat.iconBg} shrink-0 ml-3`}
                  >
                    <Icon className={`h-5 w-5 ${stat.iconColor}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-7">
          <Card className="border border-gray-200 bg-white xl:col-span-7">
            <div className="flex flex-row items-center justify-between p-5 pb-2">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Sales Performance
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Monthly comparison — your sales vs shop purchases
                </p>
              </div>
            </div>
            <div className="px-5 pb-5">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chart} barSize={24} barGap={4}>
                  <CartesianGrid
                    vertical={false}
                    stroke="#f0f0f0"
                    strokeDasharray="4 4"
                  />
                  <XAxis
                    dataKey={
                      timeRange === "monthly"
                        ? "month"
                        : timeRange === "daily"
                          ? "date"
                          : "year"
                    }
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={formatCompactCurrency}
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  />
                  <Bar
                    dataKey="purchases"
                    name="Purchases"
                    fill="#6366f1"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="sales"
                    name="Sales"
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2 justify-end px-1">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-[#6366f1] inline-block" />
                  <span className="text-sm text-gray-600">Purchases</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-[#10b981] inline-block" />
                  <span className="text-sm text-gray-600">Sales</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-7">
          <Card className="border border-gray-200 bg-white xl:col-span-3">
            <div className="p-5 pb-2">
              <h3 className="text-base font-semibold text-gray-900">
                Category Distribution
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Your sales by product category
              </p>
            </div>
            <div className="px-5 pb-5">
              {categoryDistribution.length === 0 ? (
                <div className="flex items-center justify-center h-[280px] text-sm text-gray-600">
                  No category data yet
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={categoryDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {categoryDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
                    {categoryDistribution.map((cat) => (
                      <div key={cat.name} className="flex items-center gap-1.5">
                        <span
                          className="w-2.5 h-2.5 rounded-sm"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="text-sm text-gray-600">
                          {cat.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card className="border border-gray-200 bg-white xl:col-span-4">
            <div className="p-5 pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Recent Activities
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Latest updates from your sales
                </p>
              </div>
              <div className="relative w-full sm:w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
                <Input
                  type="text"
                  placeholder="Search activities..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9 pr-3 py-1.5 h-9 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full"
                />
              </div>
            </div>
            <div className="px-5 pb-3 space-y-3">
              {recentActivities.length === 0 ? (
                <div className="text-center py-8 text-gray-600 text-sm">
                  No activities found matching your search
                </div>
              ) : (
                recentActivities.map((activity) => {
                  const getIconBg = () => {
                    switch (activity.type) {
                      case "sale":
                        return "bg-green-100 text-green-600";
                      case "stock":
                        return "bg-blue-100 text-blue-600";
                      case "closeout":
                        return "bg-purple-100 text-purple-600";
                      case "alert":
                        return "bg-red-100 text-red-600";
                      default:
                        return "bg-gray-100 text-gray-600";
                    }
                  };
                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getIconBg()}`}
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.action}
                          </p>
                          <span className="text-sm text-gray-600 whitespace-nowrap">
                            {activity.time}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">
                          {activity.description}
                        </p>
                        {activity.amount !== null &&
                          activity.amount !== undefined &&
                          activity.amount > 0 && (
                            <p className="text-sm font-semibold text-blue-600 mt-0.5">
                              {formatCurrency(activity.amount)}
                            </p>
                          )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {recentActivitiesMeta.total > 0 && (
              <div className="px-5 pb-5">
                <Pagination
                  currentPage={activitiesPage}
                  totalPages={recentActivitiesMeta.last_page || 1}
                  pageSize={activitiesPerPage}
                  totalItems={recentActivitiesMeta.total}
                  onPageChange={setActivitiesPage}
                  onPageSizeChange={(v) => {
                    setActivitiesPerPage(v);
                    setActivitiesPage(1);
                  }}
                />
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SellerHome;
